import React, { useCallback, useEffect, useState } from 'react';
import './TriTableau.css';

// ═══════════════════════════════════════════════════════════════════════════
// Tri de tableau — mécanisme partagé.
//
// Écrit une fois parce qu'il doit servir aux tableaux existants (RDV à
// confirmer, Nouveautés pièces, Suivi atelier…) autant qu'aux nouveaux :
// recopier une fonction de tri par tableau, c'est garantir qu'ils finiront par
// trier différemment — les accents dans un tableau et pas dans l'autre, les
// valeurs vides tantôt en tête tantôt en queue.
//
// Utilisation :
//   const tri = useTri('date', 'desc');
//   const lignes = tri.appliquer(donnees, { date: d => d.date_intervention });
//   …
//   <thead><tr><EnteteTri tri={tri} cle="date">Date</EnteteTri>…</tr></thead>
// ═══════════════════════════════════════════════════════════════════════════

// Comparaison d'une valeur quelconque.
//
// Trois partis pris, tous destinés à ce que le tri ne surprenne jamais :
//  · les valeurs absentes vont TOUJOURS en fin de liste, quel que soit le sens.
//    Trier par « N° OR » pour trouver les dossiers sans OR n'a aucun sens ;
//    trier pour lire les OR renseignés, si.
//  · les nombres se comparent en nombres — sinon 10 passerait avant 9.
//  · le texte se compare avec `localeCompare` en français : sans lui, « Émile »
//    se retrouve après « Zoé », ce qui donne une liste que personne ne relit.
function comparer(a, b) {
  const videA = a === null || a === undefined || a === '';
  const videB = b === null || b === undefined || b === '';
  if (videA && videB) return 0;
  if (videA) return 1;   // les vides en queue, sens de tri ignoré (voir plus bas)
  if (videB) return -1;

  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return (a ? 1 : 0) - (b ? 1 : 0);

  const na = Number(a), nb = Number(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb) && String(a).trim() !== '' && String(b).trim() !== '') {
    return na - nb;
  }
  return String(a).localeCompare(String(b), 'fr', { sensitivity: 'base', numeric: true });
}

export function useTri(cleParDefaut = null, sensParDefaut = 'asc') {
  // Un seul état plutôt que deux : la clé et le sens changent toujours ensemble,
  // et les piloter séparément obligerait à écrire un `setSens` depuis le
  // calculateur de `setCle` — un effet de bord dans un réducteur, que le mode
  // strict de React exécute deux fois.
  const [etat, setEtat] = useState({ cle: cleParDefaut, sens: sensParDefaut });
  const { cle, sens } = etat;

  // Premier clic : ascendant. Deuxième : descendant. Troisième : retour à l'ordre
  // naturel du tableau — sans quoi on ne peut plus revenir à l'ordre métier
  // (le plus urgent en premier) une fois qu'on a trié.
  const basculer = useCallback((c) => {
    setEtat(prev => {
      if (prev.cle !== c) return { cle: c, sens: 'asc' };
      if (prev.sens === 'asc') return { cle: c, sens: 'desc' };
      return { cle: null, sens: 'asc' };
    });
  }, []);

  // `accesseurs` : { cle: ligne => valeur }. Une clé sans accesseur lit la
  // propriété de même nom.
  const appliquer = useCallback((lignes, accesseurs = {}) => {
    if (!cle || !Array.isArray(lignes)) return lignes || [];
    const lire = accesseurs[cle] || ((l) => l[cle]);
    const signe = sens === 'desc' ? -1 : 1;
    // Copie : trier en place réordonnerait le tableau du parent, donc l'état React.
    return [...lignes].sort((x, y) => {
      const a = lire(x), b = lire(y);
      const videA = a === null || a === undefined || a === '';
      const videB = b === null || b === undefined || b === '';
      // Les vides restent en queue même en tri descendant : les inverser
      // remplirait le haut de l'écran de lignes sans la donnée qu'on trie.
      if (videA || videB) return comparer(a, b);
      return signe * comparer(a, b);
    });
  }, [cle, sens]);

  return { cle, sens, basculer, appliquer };
}

// En-tête cliquable. `<th>` reste un `<th>` (accessibilité, styles hérités) ;
// le bouton interne porte le clic et l'annonce du tri courant.
export function EnteteTri({ tri, cle, children, align, title }) {
  const actif = tri.cle === cle;
  const ariaSort = !actif ? 'none' : (tri.sens === 'asc' ? 'ascending' : 'descending');
  return (
    <th aria-sort={ariaSort} className={`tt-th${actif ? ' is-active' : ''}`} style={align ? { textAlign: align } : undefined}>
      <button type="button" className="tt-bouton" onClick={() => tri.basculer(cle)} title={title || 'Trier'}>
        <span>{children}</span>
        <span className="tt-fleche" aria-hidden="true">{actif ? (tri.sens === 'asc' ? '▲' : '▼') : '⇅'}</span>
      </button>
    </th>
  );
}

// Formate une ancienneté en langage courant : « 30 minutes », « 2 h 15 », « 3 jours ».
//
// Trois échelles plutôt qu'une, parce qu'un réceptionnaire ne lit pas « 4 320
// minutes » : sous l'heure on compte en minutes, dans la journée en heures et
// minutes, au-delà en jours. `depuisMs` accepte une date ISO ou un timestamp.
export function formatAnciennete(depuis, maintenant = Date.now()) {
  if (!depuis) return null;
  const t = typeof depuis === 'number' ? depuis : Date.parse(depuis);
  if (Number.isNaN(t)) return null;
  const min = Math.max(0, Math.floor((maintenant - t) / 60000));
  if (min < 1) return "à l'instant";
  if (min < 60) return `${min} minute${min > 1 ? 's' : ''}`;
  const h = Math.floor(min / 60), reste = min % 60;
  if (h < 24) return reste ? `${h} h ${String(reste).padStart(2, '0')}` : `${h} heure${h > 1 ? 's' : ''}`;
  const j = Math.floor(h / 24);
  return `${j} jour${j > 1 ? 's' : ''}`;
}

// Horloge partagée : un seul intervalle pour tout l'écran plutôt qu'un par
// ligne. Rythme d'une minute — c'est la plus petite unité affichée, rafraîchir
// plus vite ne changerait rien à l'écran tout en le faisant travailler.
export function useHorloge(periodeMs = 60000) {
  const [maintenant, setMaintenant] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setMaintenant(Date.now()), periodeMs);
    return () => clearInterval(t);
  }, [periodeMs]);
  return maintenant;
}

// Seuils d'alerte sur l'ancienneté, en heures. Volontairement affichés comme un
// dégradé discret et non comme un rouge vif : le tableau doit se lire d'un coup
// d'œil sans transformer chaque dossier un peu ancien en incident.
export function niveauAnciennete(depuis, maintenant = Date.now(), seuils = { attention: 4, alerte: 24 }) {
  if (!depuis) return '';
  const t = typeof depuis === 'number' ? depuis : Date.parse(depuis);
  if (Number.isNaN(t)) return '';
  const h = (maintenant - t) / 3600000;
  if (h >= seuils.alerte) return 'alerte';
  if (h >= seuils.attention) return 'attention';
  return '';
}

export default useTri;
